import { Toast, ToastType } from '../types/ui.types';

type ToastListener = (toasts: Toast[]) => void;

class NotificationService {
  private toasts: Toast[] = [];
  private listeners: ToastListener[] = [];
  private idCounter = 0;

  subscribe(listener: ToastListener): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(): void {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  private generateId(): string {
    return `toast-${++this.idCounter}-${Date.now()}`;
  }

  show(message: string, type: ToastType = 'info', duration: number = 5000): string {
    const id = this.generateId();
    const toast: Toast = {
      id,
      message,
      type,
      duration
    };

    this.toasts.push(toast);
    this.notify();

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  success(message: string, duration?: number): string {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration?: number): string {
    return this.show(message, 'error', duration);
  }

  info(message: string, duration?: number): string {
    return this.show(message, 'info', duration);
  }

  warning(message: string, duration?: number): string {
    return this.show(message, 'warning', duration);
  }

  remove(id: string): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  clear(): void {
    this.toasts = [];
    this.notify();
  }

  getToasts(): Toast[] {
    return [...this.toasts];
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
